<script setup lang="ts">
import type { SelectionRow } from '@/helpers/selectionRows'

import SelectionSubtotal from './SelectionSubtotal.vue'
import SelectionSummaryStep from './SelectionSummaryStep.vue'
import { openEditStepModal } from './openEditStepModal'

export type SelectionSummaryProps = {
  rows: SelectionRow[]
  /** Omitted or `null` hides the subtotal until tickets are reserved. */
  subtotal?: number | null
  title?: string
  /** Set when a host landmark names itself from this heading via `aria-labelledby`. */
  titleId?: string
  /** `plain` for modal/bottom-sheet hosts that already provide a surface. */
  surface?: 'card' | 'plain'
  emptyMessage?: string
}

const props = withDefaults(defineProps<SelectionSummaryProps>(), {
  subtotal: undefined,
  title: undefined,
  titleId: undefined,
  surface: 'card',
  emptyMessage: 'Nothing selected yet',
})

const emit = defineEmits<{
  edit: [resolverId: string, type?: string]
}>()

function handleEdit(resolverId: string, type?: string) {
  openEditStepModal({
    onConfirm: () => emit('edit', resolverId, type),
  })
}

function stepKey(row: SelectionRow, index: number): string {
  return `${row.resolverId}-${index}`
}
</script>

<template>
  <div class="selection-summary" :class="surface">
    <div v-if="title" class="header">
      <h2 :id="props.titleId" class="title">{{ title }}</h2>
    </div>

    <div class="body">
      <p v-if="rows.length === 0" class="empty-message">{{ props.emptyMessage }}</p>

      <TransitionGroup v-else appear aria-label="Confirmed selections" class="steps" name="step" tag="ol">
        <SelectionSummaryStep
          v-for="(row, index) in rows"
          :key="stepKey(row, index)"
          :first="index === 0"
          :row="row"
          :style="{ '--index': index }"
          @edit="handleEdit"
        />
      </TransitionGroup>

      <SelectionSubtotal v-if="props.subtotal != undefined" :value="props.subtotal" />
    </div>
  </div>
</template>

<style scoped>
.selection-summary {
  display: flex;
  flex-direction: column;

  &.card {
    border: 1px solid var(--card-border);
    border-radius: var(--radius-medium);
    background-color: var(--card-bg);
    box-shadow: var(--shadow-md);
    overflow: hidden;

    .header {
      border-bottom: 1px solid var(--card-border);
      background-color: var(--card-header-bg);
    }

    .body {
      padding: var(--padding-compact);
    }
  }
}

.header {
  display: flex;
  align-items: center;
  padding: var(--padding-compact);
}

.title {
  font-family: var(--font-family-heading);
  font-size: var(--font-size-h2);
  font-weight: var(--font-weight-bold);
  line-height: 1.2;
  color: var(--card-header-text);
}

.body {
  display: flex;
  flex-direction: column;
  gap: var(--padding-comfy);
}

.empty-message {
  font-size: var(--font-size-body-small);
  line-height: var(--line-height-body);
  color: var(--text-secondary);
  text-align: center;
}

.steps {
  display: flex;
  flex-direction: column;
  list-style: none;
  padding-left: 0;
}

.step-enter-active {
  transition:
    opacity 0.4s ease,
    transform 0.4s ease;
  transition-delay: calc(180ms * (var(--index, 0) / (var(--index, 0) + 2)));
}

.step-enter-from {
  opacity: 0;
  transform: translateY(30px);
}
</style>
